import { useEffect, useState, type ReactNode } from "react";
import { PanelLeftClose } from "lucide-react";
import { useT } from "../../lib/i18n";
import { cx } from "../../lib/cx";
import {
  DS_SIDEBAR_STORAGE_KEY,
  readSidebarCollapsed,
  writeSidebarCollapsed,
} from "./model";
import { dsControl, dsFont } from "./styles";

export type DsNavItem = {
  id: string;
  label: string;
  icon: ReactNode;
  active?: boolean;
  badge?: string;
  disabled?: boolean;
  onSelect?: () => void;
};

export type DsNavGroup = {
  id: string;
  label: string;
  items: DsNavItem[];
};

export type DsSidebarUser = {
  name: string;
  detail: string;
  initials: string;
};

type Props = {
  wordmark: string;
  wordmarkAccent?: string;
  mark: string;
  groups: DsNavGroup[];
  settings: DsNavItem;
  user: DsSidebarUser;
  /** When set, the collapsed flag is controlled. Storage is still updated. */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  storageKey?: string;
};

function useWideSidebar(): boolean {
  const [wide, setWide] = useState(true);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(min-width: 1181px)");
    const sync = () => setWide(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return wide;
}

function NavButton({ item, iconOnly }: { item: DsNavItem; iconOnly: boolean }) {
  return (
    <span className="group relative block">
      <button
        type="button"
        title={item.label}
        aria-current={item.active ? "page" : undefined}
        aria-disabled={item.disabled || undefined}
        data-testid={`ds-nav-${item.id}`}
        className={cx(
          dsControl,
          "flex min-h-[34px] w-full items-center gap-2.5 rounded-ds-pill px-2.5 py-2 text-left text-ds-body hover:bg-ds-hover hover:text-ds-ink",
          item.active
            ? "bg-ds-glass-strong font-medium text-ds-ink shadow-[0_8px_20px_-12px_rgba(60,60,90,0.4)]"
            : "font-normal text-ds-ink-2",
          iconOnly && "justify-center px-0",
        )}
        onClick={() => {
          if (item.disabled) return;
          item.onSelect?.();
        }}
      >
        <span className={cx("grid size-[17px] shrink-0 place-items-center", item.active && "text-ds-teal")}>
          {item.icon}
        </span>
        <span className={cx("min-w-0 flex-1 truncate", iconOnly && "sr-only")}>{item.label}</span>
        {item.badge ? (
          <span
            className={cx(
              "shrink-0 rounded-ds-pill border border-solid border-ds-hair px-[7px] py-0.5 text-ds-badge-sm font-normal text-ds-ink-3",
              iconOnly && "hidden",
            )}
          >
            {item.badge}
          </span>
        ) : null}
      </button>
      {iconOnly ? (
        <span
          role="tooltip"
          className="pointer-events-none absolute top-1/2 left-full z-20 ml-2 hidden -translate-y-1/2 rounded-ds-pill bg-ds-ink px-2.5 py-1 font-ds text-ds-meta font-normal whitespace-nowrap text-ds-bg-a group-hover:block group-focus-within:block"
        >
          {item.label}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Flush, full-height sidebar. Collapsed state is icons plus tooltips and is
 * remembered per browser. At ≤1180px it stacks full width with labels visible.
 */
export function Sidebar({
  wordmark,
  wordmarkAccent,
  mark,
  groups,
  settings,
  user,
  collapsed: collapsedProp,
  onCollapsedChange,
  storageKey = DS_SIDEBAR_STORAGE_KEY,
}: Props) {
  const t = useT();
  const wide = useWideSidebar();
  const [stored, setStored] = useState(() => readSidebarCollapsed(storageKey));
  const collapsed = collapsedProp ?? stored;
  const iconOnly = collapsed && wide;

  function setCollapsed(next: boolean) {
    setStored(next);
    writeSidebarCollapsed(next, storageKey);
    onCollapsedChange?.(next);
  }

  return (
    <aside
      data-ds=""
      data-testid="ds-sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      aria-label={t("ds.sidebar.nav")}
      className={cx(
        dsFont,
        // Fills the gallery shell (100dvh) so the user block stays at the
        // bottom of the viewport. At ≤1180px the bar stacks and sizes to content.
        "flex h-full min-h-0 shrink-0 flex-col self-stretch border-r border-solid border-ds-hair bg-ds-glass backdrop-blur-ds backdrop-saturate-[1.1]",
        "max-[1180px]:h-auto",
        iconOnly ? "w-ds-sidebar-collapsed" : "w-ds-sidebar",
        "max-[1180px]:w-full max-[1180px]:border-r-0 max-[1180px]:border-b",
      )}
    >
      <div
        data-testid="ds-sidebar-panel"
        className={cx(
          "sticky top-0 flex h-full min-h-0 flex-col px-3 py-4",
          iconOnly && "px-2.5",
          "max-[1180px]:static max-[1180px]:h-auto",
        )}
      >
      <div className={cx("flex items-center justify-between gap-1.5 pb-2.5", iconOnly && "flex-col gap-2 pb-2")}>
        <div className="flex min-w-0 items-center gap-2 text-[16px] font-semibold whitespace-nowrap text-ds-ink">
          <span
            className={cx(
              "hidden size-[30px] place-items-center rounded-[10px] bg-ds-teal text-ds-item text-white",
              iconOnly && "grid",
            )}
            aria-hidden
          >
            {mark}
          </span>
          <span className={cx(iconOnly && "sr-only")}>
            {wordmark}
            {wordmarkAccent ? <b className="font-semibold text-ds-teal">{wordmarkAccent}</b> : null}
          </span>
        </div>
        <button
          type="button"
          data-testid="ds-sidebar-collapse"
          aria-expanded={!collapsed}
          aria-label={collapsed ? t("ds.sidebar.expand") : t("ds.sidebar.collapse")}
          title={collapsed ? t("ds.sidebar.expand") : t("ds.sidebar.collapse")}
          className={cx(
            dsControl,
            "grid size-[30px] shrink-0 place-items-center rounded-full p-0 text-ds-ink-3 hover:bg-ds-hover hover:text-ds-ink",
          )}
          onClick={() => setCollapsed(!collapsed)}
        >
          <PanelLeftClose className={cx("size-4", iconOnly && "-scale-x-100")} aria-hidden />
        </button>
      </div>

      <nav className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {groups.map((group) => (
            <div key={group.id}>
              <div
                className={cx(
                  "mx-2.5 mt-3 mb-1 text-[11px] font-normal text-ds-ink-3",
                  iconOnly && "mx-0 h-0 overflow-hidden border-t border-solid border-ds-hair",
                )}
              >
                <span className={cx(iconOnly && "sr-only")}>{group.label}</span>
              </div>
              <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                {group.items.map((item) => (
                  <li key={item.id}>
                    <NavButton item={item} iconOnly={iconOnly} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-auto shrink-0 pt-3.5">
          <ul className="m-0 list-none p-0">
            <li>
              <NavButton item={settings} iconOnly={iconOnly} />
            </li>
          </ul>
          <div
            className={cx(
              "mx-1.5 mt-2 flex items-center gap-2.5 border-t border-solid border-ds-hair pt-3",
              iconOnly && "justify-center",
            )}
            title={`${user.name}, ${user.detail}`}
            data-testid="ds-user"
          >
            <i className="grid size-8 shrink-0 place-items-center rounded-full bg-ds-glass-strong text-ds-badge font-semibold text-ds-ink not-italic">
              {user.initials}
            </i>
            <div className={cx("min-w-0 text-ds-body font-semibold text-ds-ink", iconOnly && "hidden")}>
              <span className="block truncate">{user.name}</span>
              <small className="block truncate text-ds-badge font-normal text-ds-ink-3">{user.detail}</small>
            </div>
          </div>
        </div>
      </nav>
      </div>
    </aside>
  );
}
